import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import useUserData from "../../custom_hooks/useUserData";
import axiosInstance from "../../API/axiosConfig";
import webserviceUrl from "../../API/webserviceUrl";
import Table from "../../core/common/dataTable/index";
import {
  MdEdit,
  MdDelete,
  MdRemoveRedEye,
  MdFileDownload,
} from "react-icons/md";
import { AiFillFile } from "react-icons/ai";
import DeleteModal from "../../core/modals/deleteModal";
import { toast } from "react-toastify";
import UnitForm from "./UnitForm";
import LoadingGreen from "../loading/LoadingGreen";

const AddLessonPlan = () => {
  const { employee_id, school_code, course } = useUserData();
  const [isUpdatingForm, setIsUpdatingForm] = useState(false);
  const [openAddModal, setOpenAddModal] = useState<boolean>(true);
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [units, setUnits] = useState([]);
  const [resError, setResError] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const initialFormData = {
    unit: "",
    topic: "",
    plan_number: "",
    lesson_pdf_path: null,
    school_code: school_code,
    employee_id: employee_id,
    participant_type: course?.level ?? "",
    participant_tier: course?.class ?? "",
    course_group: course?.combination_code ?? "",
    batch_no: course?.section ?? "",
    subject: course?.course_name,
    course_id: course?.acdmc_course_id,
    lesson_resource: null,
    lesson_video_path: null,
  };

  const [formData, setFormData] = useState<any>(initialFormData);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLInputElement | null>(null);
  const zipRef = useRef<HTMLInputElement | null>(null);
  console.log(initialFormData);

  // Cohorts (batches) come from the get_batches API
  const [cohortOptions, setCohortOptions] = useState<any[]>([]);
  const [cohortLoading, setCohortLoading] = useState(false);

  // Fetch batches and use them as the cohort dropdown options
  const getCohorts = async () => {
    if (!school_code) return; // safety check
    try {
      setCohortLoading(true);
      const resp = await axiosInstance.post(webserviceUrl.get_batches, {
        employee_id,
        school_code,
      });

      const batchData = resp?.data?.data || [];
      setCohortOptions(batchData);
    } catch (error) {
      console.error("Error fetching cohorts:", error);
    } finally {
      setCohortLoading(false);
    }
  };

  useEffect(() => {
    getCohorts();
  }, [school_code]);

  // Picking a course in UnitForm updates the course-derived fields on
  // formData directly, for both the "Add" and "Edit" flows.
  const handleCourseSelect = (selected: any) => {
    localStorage.setItem("cohort", JSON.stringify(selected));
    localStorage.setItem("course", JSON.stringify(selected));
    localStorage.setItem("batch", btoa(JSON.stringify(selected)));

    setFormData((prev: any) => ({
      ...prev,
      participant_type: selected?.level ?? prev.participant_type,
      participant_tier: selected?.class ?? prev.participant_tier,
      course_group: selected?.combination_code ?? prev.course_group,
      batch_no: selected?.section ?? prev.batch_no,
      subject: selected?.course_name ?? prev.subject,
      course_id: selected?.acdmc_course_id ?? prev.course_id,
    }));
    setErrors((prev: any) => ({ ...prev, course_id: "" }));
  };

  // FIX: this effect used to run unconditionally on every change of
  // `course` (which is a new object reference almost every render from
  // useUserData()). That meant right after the Edit-icon click set
  // formData from the clicked record, this effect fired again and
  // overwrote subject/course_id (and caused topic/unit to appear wiped
  // during the re-render) with the logged-in user's default course
  // instead of the record being edited. Now it only syncs the
  // course-derived fields when we're NOT mid-edit.
  useEffect(() => {
    if (isUpdatingForm) return;
    if (course && Object.keys(course).length > 0) {
      setFormData((prevFormData: any) => ({
        ...prevFormData,
        school_code: school_code,
        employee_id: employee_id,
        participant_type: course?.level ?? "",
        participant_tier: course?.class ?? "",
        course_group: course?.combination_code ?? "",
        batch_no: course?.section ?? "",
        subject: course?.course_name ?? "",
        course_id: course?.acdmc_course_id ?? "",
      }));
    }
  }, [course, school_code, employee_id, isUpdatingForm]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "key",
      render: (text: string, record: any, index: number) => <p>{index + 1}</p>,
    },
    { title: "Lesson Topic", dataIndex: "topic" },
    { title: "Lesson Unit", dataIndex: "unit" },
    { title: "Day Number", dataIndex: "plan_number" },
    {
      title: "Document",
      dataIndex: "lesson_pdf_path",
      render: (text: string) =>
        text ? (
          <a
            href={text}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info tn-link"
          >
            View PDF
          </a>
        ) : (
          "Not uploaded"
        ),
    },
    {
      title: "Video Resource",
      dataIndex: "session_recording",
      render: (text: string) =>
        text ? (
          <a
            href={text}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info tn-link"
          >
            <MdRemoveRedEye style={{ cursor: "pointer", fontSize: "18px" }} />
          </a>
        ) : (
          "Not uploaded"
        ),
    },
    {
      title: "Training Zip",
      dataIndex: "resources_zip",
      render: (text: string) =>
        text ? (
          <a
            href={text}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="text-info tn-link"
            title="Download Zip"
          >
            <MdFileDownload style={{ cursor: "pointer", fontSize: "18px" }} />
          </a>
        ) : (
          "Not uploaded"
        ),
    },
    {
      title: "Sub-units",
      dataIndex: "lesson_plan_id",
      key: "lesson_plan_id",
      render: (text: string, record: any) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <AiFillFile
            style={{ cursor: "pointer", color: "#549a3d" }}
            onClick={() => {
              navigate(`/add-subunit/${record.lesson_plan_id}`);
            }}
          />
        </div>
      ),
    },
    {
      title: "Actions",
      dataIndex: "lesson_plan_id",
      key: "lesson_plan_id",
      render: (text: string, record: any) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <MdEdit
            style={{ cursor: "pointer", color: "#007bff" }}
            onClick={() => {
              setIsUpdatingForm(true);
              if (fileRef.current) {
                fileRef.current.value = "";
              }
              if (videoRef.current) {
                videoRef.current.value = "";
              }
              if (zipRef.current) {
                zipRef.current.value = "";
              }
              setOpenAddModal(true);

              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });

              setFormData((prev: any) => ({
                ...prev,
                topic: record?.topic,
                unit: record?.unit,
                plan_number: record?.plan_number,
                lesson_pdf_path: record?.lesson_pdf_path,
                lesson_resource: record?.resources_zip,
                lesson_video_path: record?.session_recording,
                participant_type:
                  record?.participant_type ??
                  record?.level ??
                  prev.participant_type,
                participant_tier:
                  record?.participant_tier ??
                  record?.class ??
                  prev.participant_tier,
                course_group:
                  record?.course_group ??
                  record?.combination_code ??
                  prev.course_group,
                batch_no: record?.batch_no ?? record?.section ?? prev.batch_no,
                subject: record?.subject ?? record?.course_name ?? prev.subject,
                course_id:
                  record?.course_id ??
                  record?.acdmc_course_id ??
                  prev.course_id,
                school_code: school_code,
                employee_id: employee_id,
                lesson_plan_id: record?.lesson_plan_id,
              }));
            }}
          />
          <MdDelete
            style={{ cursor: "pointer", color: "#dc3545" }}
            data-bs-toggle="modal"
            data-bs-target="#delete_modal"
            onClick={() => setSelectedRecord(record)}
          />
        </div>
      ),
    },
  ];

  console.log(formData);

  const validateForm = () => {
    let newErrors: any = {};
    if (!formData?.course_id) newErrors.course_id = "Please select a course";
    if (!formData?.topic?.trim()) newErrors.topic = "Lesson topic is required";
    if (!formData?.unit?.trim()) newErrors.unit = "Lesson unit is required";
    if (
      !formData?.lesson_pdf_path ||
      (Array.isArray(formData.lesson_pdf_path) &&
        formData.lesson_pdf_path.length === 0)
    ) {
      newErrors.lesson_pdf_path =
        "Please upload at least one training presentation PDF";
    }
    if (!formData?.plan_number)
      newErrors.plan_number = "Training day number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: any) => {
    const { name, value, files } = e.target;

    if (files !== null) {
      setFormData((prevData: any) => ({
        ...prevData,
        [name]: Array.from(files),
      }));
    } else {
      setFormData((prevData: any) => ({
        ...prevData,
        [name]: value,
      }));
    }

    setErrors((prev: any) => ({
      ...prev,
      [name]: "",
    }));
  };

  const getUnits = async () => {
    try {
      setLoading(true);
      setResError({});
      const resp = await axiosInstance.post(webserviceUrl.view_units, {
        employee_id,
        school_code,
      });
      if (resp?.data?.success) {
        const unitData = resp?.data?.data || [];
        setUnits(unitData);
      } else {
        setResError(resp?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };
  useEffect(() => {
    getUnits();
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmittingForm(true);
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]: [string, any]) => {
        if (value == null) return;

        if (key === "lesson_pdf_path" && Array.isArray(value)) {
          value.forEach((file: File) => {
            data.append("lesson_pdf_path", file);
          });
        } else {
          data.append(key, value);
        }
      });

      const apiUrl = isUpdatingForm
        ? webserviceUrl.update_units
        : webserviceUrl.add_units;
      const resp = await axiosInstance.post(apiUrl, data);
      if (resp?.data?.success) {
        toast.success(resp?.data?.message);
        setTimeout(() => {
          setIsSubmittingForm(false);
        }, 500);

        setFormData((prev: any) => ({
          ...prev,
          unit: "",
          topic: "",
          plan_number: "",
          lesson_pdf_path: null,
          lesson_resource: null,
          lesson_video_path: null,
          participant_type: course?.level ?? "",
          participant_tier: course?.class ?? "",
          course_group: course?.combination_code ?? "",
          batch_no: course?.section ?? "",
          subject: course?.course_name ?? "",
          course_id: course?.acdmc_course_id ?? "",
        }));
        if (fileRef.current) {
          fileRef.current.value = "";
        }
        if (videoRef.current) {
          videoRef.current.value = "";
        }
        if (zipRef.current) {
          zipRef.current.value = "";
        }
        setIsUpdatingForm(false);
        getUnits();
      }
    } catch (error) {
      toast.error("Check form details.");
      console.log(error);
    } finally {
      setTimeout(() => {
        setIsSubmittingForm(false);
      }, 500);
    }
  };

  const handleDelete = async () => {
    try {
      const payload = {
        school_code: formData?.school_code,
        lesson_plan_id: selectedRecord?.lesson_plan_id,
      };

      const resp = await axiosInstance.post(
        webserviceUrl.delete_units,
        payload,
      );

      if (resp?.data?.success) {
        getUnits();
        toast.success(resp?.data?.message);
      }
    } catch (error) {
      console.error("Error in handleDelete:", error);
      toast.error("Failed to delete. Please try again.");
    }
  };

  return (
    <>
      <DeleteModal handleDelete={handleDelete} />
      <div className="page-wrapper">
        <div className="content">
          <div>
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
              <div className="my-auto mb-2">
                <h2 className="mb-1">Lesson Plan Units</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.adminDashboard}>
                        <i className="ti ti-smart-home" />
                      </Link>
                    </li>
                    <li className="breadcrumb-item">Teacher</li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Lesson Plan Units
                    </li>
                  </ol>
                </nav>
              </div>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={() => {
                    setIsUpdatingForm(false);
                    setOpenAddModal(true);
                  }}
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Lesson Plan
                </button>
              </div>
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-3">
              <div className=" pe-2 mb-2 action-icon d-flex justify-content-between">
                <h3>{isUpdatingForm ? "Edit" : "Add"} Lesson Plan</h3>
                <i className="ti ti-x fs-16 p-1 border-secondary-subtle border rounded-1"></i>
              </div>
              <UnitForm
                fileRef={fileRef}
                videoRef={videoRef}
                zipRef={zipRef}
                formData={formData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                errors={errors}
                isSubmitting={isSubmittingForm}
                cohortOptions={cohortOptions}
                cohortLoading={cohortLoading}
                onSelectCourse={handleCourseSelect}
                onOpenCourseDropdown={getCohorts}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-body p-3">
              <h5>{course?.course_name} Units</h5>
              {loading ? (
                <LoadingGreen size="40" paddingTop="30px" />
              ) : Object.entries(resError)?.length > 0 ? (
                <p>-- No data found --</p>
              ) : (
                <Table columns={columns} dataSource={units} Selection={false} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddLessonPlan;
